import Link from 'next/link';
import WorldMap from '@/components/home/WorldMap';
import { countriesData } from '@/data/countriesData';
import CountryImage from '@/components/shared/CountryImage';
import { HOME, mediaPath } from '@/lib/media';
import SeoHead from '@/components/seo/SeoHead';
import { breadcrumbJsonLd, seoFromPage } from '@/lib/seo';
import { formatProse } from '@/lib/formatCopy';

const formatCardText = (text) => (text ? formatProse(text) : '');

const FONT_STYLE = `
    .directory-grain::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
        pointer-events: none;
        z-index: 10;
    }
`;

export default function CountriesDirectory() {
    const seo = seoFromPage('countries');

    return (
        <>
            <SeoHead
                {...seo}
                jsonLd={breadcrumbJsonLd([
                    { name: 'Home', path: '/' },
                    { name: 'Countries', path: '/countries' },
                ])}
            />
            <style>{FONT_STYLE}</style>

            <main className="min-h-screen bg-[#F4EFE6] directory-grain overflow-x-hidden">

                {/* ── Cinematic Header ── */}
                <section className="relative h-[50vh] sm:h-[55vh] max-h-[600px] min-h-[320px] bg-[#0F1C2E] flex flex-col justify-end pb-12 sm:pb-16 md:pb-20 overflow-hidden">
                    <div className="absolute inset-0">
                        <img
                            src={mediaPath(HOME.bannerHero)}
                            alt={HOME.bannerHeroAlt}
                            className="w-full h-full object-cover object-center opacity-35 grayscale-[40%] sepia-[15%]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1C2E] via-[#0F1C2E]/60 to-[#0F1C2E]/20" />
                    </div>

                    <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 md:px-12 relative z-20">
                        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 md:mb-8">
                            <div className="w-8 sm:w-12 h-px bg-[#DF6D2A]" />
                            <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#C9A84C]">The Destinations</span>
                        </div>

                        <h1 className="font-display text-[36px] sm:text-[52px] md:text-[72px] lg:text-[90px] leading-[0.9] text-[#F4EFE6] tracking-[-0.02em] uppercase">
                            The Global <br /> <em className="text-accent-readable italic pr-4">Diaspora</em>
                        </h1>
                    </div>
                </section>

                {/* ── Interactive Map ── */}
                <div className="relative z-20 shadow-[0_-16px_32px_rgba(15,28,46,0.4)]">
                    <WorldMap />
                </div>

                {/* ── Country Cards ── */}
                <section className="py-16 sm:py-24 md:py-32 relative bg-[#F4EFE6]">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12">

                        <div className="mb-12 sm:mb-16 md:mb-20 text-center max-w-[800px] mx-auto">
                            <h2 className="font-display text-[34px] sm:text-[44px] md:text-[56px] text-[#0F1C2E] leading-none mb-4 sm:mb-6">Directory of Nations</h2>
                            <p className="font-body text-[14px] sm:text-[16px] text-[#4C596B] font-light leading-[1.7] sm:leading-normal">
                                Explore the individual historical records, timelines, and archival galleries for each destination. Select a dossier below to examine the specific legacy of that region.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8 items-stretch">
                            {countriesData.map((country) => (
                                <Link
                                    href={`/countries/${country.slug}`}
                                    key={country.slug}
                                    className="group block h-full"
                                >
                                    <article className="h-full flex flex-col bg-white border border-[#0F1C2E]/10 shadow-[0_8px_30px_rgba(15,28,46,0.06)] hover:shadow-[0_20px_50px_rgba(15,28,46,0.12)] transition-shadow duration-300 overflow-hidden">
                                        <div className="aspect-square w-full shrink-0 overflow-hidden border-b border-[#0F1C2E]/08">
                                            <CountryImage
                                                src={country.cardImage}
                                                alt={`${country.name}, Girmitiya nation card`}
                                                variant="card"
                                                imgClassName="grayscale-[20%] sepia-[8%] group-hover:grayscale-0 group-hover:sepia-0 transition-[filter] duration-500"
                                            />
                                        </div>

                                        <div className="relative flex-1 flex flex-col bg-[#FDFBF7] px-4 sm:px-5 pt-5 sm:pt-6 pb-5 sm:pb-6 border-t border-[#0F1C2E]/08 min-h-[190px]">
                                            <div className="absolute top-4 right-4 sm:top-5 sm:right-5 w-9 h-9 rounded-full border border-[#0F1C2E]/12 flex items-center justify-center bg-white shadow-sm group-hover:bg-[#DF6D2A] group-hover:border-[#DF6D2A] transition-colors duration-300">
                                                <span className="text-[#0F1C2E] group-hover:text-white text-[15px] leading-none inline-block -rotate-45 transition-colors duration-300">
                                                    &rarr;
                                                </span>
                                            </div>

                                            <p className="font-body text-[10px] tracking-[0.22em] uppercase text-accent-readable font-medium mb-3 pr-12">
                                                Est. {country.arrivalDate}
                                            </p>

                                            <div className="mb-3 pr-12">
                                                <h3 className="font-display text-[26px] sm:text-[28px] text-[#0F1C2E] leading-[1.05] group-hover:text-[#DF6D2A] transition-colors duration-300">
                                                    {country.name}
                                                </h3>
                                                {country.region && (
                                                    <p className="font-body text-[11px] tracking-[0.14em] uppercase text-muted-readable mt-1.5 italic">
                                                        ({country.region})
                                                    </p>
                                                )}
                                            </div>

                                            <p className="font-body text-[13px] sm:text-[14px] text-[#4C596B]/90 font-light leading-[1.7] line-clamp-3 mt-auto">
                                                {formatCardText(country.subtitle || country.description)}
                                            </p>
                                        </div>
                                    </article>
                                </Link>
                            ))}
                        </div>

                    </div>
                </section>

                {/* ── Global Impact Footer ── */}
                <section className="bg-[#0F1C2E] py-16 sm:py-24 md:py-32 text-center relative border-t border-[#C9A84C]/20">
                    <div className="max-w-[800px] mx-auto px-4 sm:px-6">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border border-[#DF6D2A] flex items-center justify-center mx-auto mb-5 sm:mb-8">
                            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#DF6D2A] rounded-full" />
                        </div>
                        <h2 className="font-display text-[24px] sm:text-[32px] md:text-[48px] text-[#F4EFE6] leading-[1.2] mb-5 sm:mb-8">
                            &ldquo;A single journey scattered seeds across a dozen shores.&rdquo;
                        </h2>
                        <p className="font-body text-[11px] sm:text-[13px] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#C9A84C]">
                            Approximately 1.3 Million Souls • 16 Colonies • 1 Shared Legacy
                        </p>
                    </div>
                </section>

            </main>
        </>
    );
}
